import { FC } from "react";
import { usePositionsPanelContext } from "./position-panel-context";
import {
  ActionButton,
  ActionButtonGroup,
} from "./components/action-button-group";
import { PositionAsset } from "./types";
import {
  disjoinSymbols,
  getLiquidationPriceState,
  getNonCollateralAssets,
} from "./utils";
import { LoanAsset } from "./components/loan-asset";
import { ExternalLink } from "../link";
import { AprDisplay } from "../apr-display";
import { DescriptionExternalLink } from "../description";

export const BorrowingDisabled = () => {
  const { links } = usePositionsPanelContext();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2.5">
        <h2 className="font-medium text-white">
          Borrowing is currently disabled.
        </h2>
        <p className="text-shale-200">
          You can{"'"}t open a borrow position when you have deposited all of
          the silo{"'"}s assets.{" "}
        </p>
      </div>

      <div>
        <ExternalLink
          variant="action"
          className="inline-block lg:text-base"
          href={links.disabledBorrowing}
          size="small"
        />
      </div>
    </div>
  );
};

export const NoBorrows = () => {
  const { positions, links } = usePositionsPanelContext();
  const nonCollateralAssets = getNonCollateralAssets(positions);
  const borrowLink = links.getBorrowLink(
    positions.siloAddress,
    nonCollateralAssets[0]?.assetAddress
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2.5">
        <h2 className="font-medium text-white">
          You don{"'"}t have any positions yet.
        </h2>
        <p className="text-shale-200">
          Borrow some {disjoinSymbols(nonCollateralAssets)} to see your position
          here.
        </p>
      </div>

      <ActionButtonGroup>
        <ActionButton href={borrowLink} data-cy="borrow-card-borrow-button">
          Borrow
        </ActionButton>
      </ActionButtonGroup>
    </div>
  );
};

interface BorrowAssetProps {
  asset: PositionAsset;
  rewardsSymbol: string;
  divider?: boolean;
}

export const BorrowAsset: FC<BorrowAssetProps> = ({
  asset,
  rewardsSymbol,
  divider = false,
}) => {
  const { positions, useQuotePrice, links, afterActionGroupSlot } =
    usePositionsPanelContext();
  const quotePrice = useQuotePrice();
  const liquidationPriceState = getLiquidationPriceState({
    positions,
    asset,
    quotePrice,
  });

  const {
    debtBalance,
    decimals,
    assetAddress,
    borrowApr,
    borrowRewardsApr,
    isBorrowAprFixed,
    symbol,
    merklRewards,
    points,
  } = asset;

  const borrowLink = links.getBorrowLink(
    positions.siloAddress,
    asset.assetAddress
  );
  const repayLink = links.getRepayLink(
    positions.siloAddress,
    asset.assetAddress
  );

  const debtPoints = points.filter(({ type }) => type === "debt");

  return (
    <div className="flex flex-col gap-5 @screen-sm:gap-8">
      <LoanAsset
        assetAddress={assetAddress}
        symbol={symbol}
        decimals={decimals}
        balance={debtBalance}
        balanceType="Borrow"
        balanceLabel="Borrowed"
        aprSlot={
          <AprDisplay.PositionBorrow
            borrowApr={borrowApr}
            borrowRewardsApr={borrowRewardsApr}
            rewardsSymbol={rewardsSymbol}
            isBorrowAprFixed={isBorrowAprFixed}
            merklRewards={merklRewards}
          />
        }
        liquidationPriceState={liquidationPriceState}
        liquidationPriceDescription={<LiquidationPriceDescription />}
        divider={divider}
        points={debtPoints}
      />

      {divider && <div className="h-px bg-shale-700" />}

      <ActionButtonGroup>
        <ActionButton href={borrowLink} data-cy="borrow-card-borrow-button">
          Borrow more
        </ActionButton>

        <ActionButton href={repayLink} data-cy="borrow-card-repay-button">
          Repay
        </ActionButton>
      </ActionButtonGroup>

      {afterActionGroupSlot}
    </div>
  );
};

const LiquidationPriceDescription: FC = () => {
  const { links } = usePositionsPanelContext();

  return (
    <div className="flex flex-col gap-5">
      <p>
        This is the simulated asset price at which the borrow position becomes
        insolvent (HF goes to 0%).
      </p>

      <p>
        If you have borrowed two assets, the liquidation price for each asset is
        weighted to reflect its impact on the position.
      </p>

      <div>
        <DescriptionExternalLink
          variant="action"
          className="inline-block lg:text-base"
          href={links.liquidationPrice}
        />
      </div>
    </div>
  );
};
